// Type-safe API client. All methods return Promise<T> and throw ApiError on
// non-2xx responses. 401 auto-redirects to /login.

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public body?: unknown,
  ) {
    super(message)
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
  })
  if (res.status === 401) {
    if (!location.pathname.startsWith('/login')) {
      window.location.href = '/login'
    }
    throw new ApiError(401, 'unauthorized')
  }
  if (!res.ok) {
    const body = await res.json().catch(() => null)
    throw new ApiError(res.status, (body as { error?: string } | null)?.error ?? res.statusText, body)
  }
  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

// ─── Types ────────────────────────────────────────────────────────────────

export interface User {
  id: number
  email: string
  name: string
}

export interface Stats {
  users: number
  active_sessions: number
}

export interface VersionInfo {
  commit: string
  built_at: string
}

export interface APIKey {
  id: number
  name: string
  key_prefix: string
  disabled: boolean
  last_used_at?: number
  created_at: number
}

export interface PoolStats {
  inflight: number
  served_total: number
}

export interface BrowserSession {
  id: string
  url: string
  started_at: string
}

export interface FetchRequest {
  url: string
  referer?: string
  wait_for?: string
  actions?: unknown[]
  extract?: {
    fields?: Record<string, string>
    html?: boolean
    screenshot?: boolean
    screenshot_full_page?: boolean
  }
  viewport?: { w: number; h: number }
  user_agent?: string
  headers?: Record<string, string>
  block_resources?: string[]
  disable_javascript?: boolean
  cookie_jar?: string
  timeout_ms: number
}

export interface CookieJar {
  name: string
  count: number
  updated_at: number
  created_at: number
}

export interface FetchResponse {
  job_id: string
  ok: boolean
  final_url?: string
  http_status?: number
  title?: string
  extracted?: Record<string, string>
  html?: string
  screenshot_b64?: string
  console?: { level: string; text: string }[]
  actions_log?: { index: number; type: string; ok: boolean; ms: number; error?: string }[]
  timing: { nav_ms: number; actions_ms: number; extract_ms: number; total_ms: number }
  error?: { code: string; message: string; at_action?: number }
}

// ─── API ──────────────────────────────────────────────────────────────────

export const api = {
  // Auth
  login: (email: string, password: string) =>
    request<User>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  logout: () => request<void>('/auth/logout', { method: 'POST' }),
  me: () => request<User>('/auth/me'),

  // Meta
  health: () => request<{ ok: boolean }>('/api/health'),
  version: () => request<VersionInfo>('/api/version'),
  stats: () => request<Stats>('/api/stats'),
  poolStats: () => request<PoolStats>('/api/pool/stats'),

  // API keys
  apiKeys: {
    list: () => request<{ items: APIKey[] }>('/api/admin/api-keys'),
    create: (name: string) =>
      request<{ item: APIKey; plain: string }>('/api/admin/api-keys', {
        method: 'POST',
        body: JSON.stringify({ name }),
      }),
    patch: (id: number, disabled: boolean) =>
      request<void>(`/api/admin/api-keys/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ disabled }),
      }),
    delete: (id: number) =>
      request<void>(`/api/admin/api-keys/${id}`, { method: 'DELETE' }),
  },

  // Browser test + live sessions
  browser: {
    test: (req: FetchRequest) =>
      request<FetchResponse>('/api/admin/browser/test', {
        method: 'POST',
        body: JSON.stringify(req),
      }),
    sessions: () =>
      request<{ items: BrowserSession[] }>('/api/admin/browser/sessions'),
    screenshotURL: (id: string) =>
      `/api/admin/browser/sessions/${encodeURIComponent(id)}/screenshot?ts=${Date.now()}`,
    jars: () => request<{ items: CookieJar[] }>('/api/admin/browser/jars'),
    jarImport: (name: string, cookies: unknown) =>
      request<void>(`/api/admin/browser/jars/${encodeURIComponent(name)}`, {
        method: 'POST',
        body: JSON.stringify(cookies),
      }),
    jarDelete: (name: string) =>
      request<void>(`/api/admin/browser/jars/${encodeURIComponent(name)}`, {
        method: 'DELETE',
      }),
  },
}
